import { Avatar } from "primereact/avatar";
import * as Token from "../../api/auth_token";
import { Tooltip } from "primereact/tooltip";
import styles from "./Avatar.module.css";
import classNames from "classnames";
import * as jdenticon from "jdenticon";
import { RefObject, useRef } from "react";
import { Menu } from "primereact/menu";
import { v4 as uuid } from "uuid";
import { useHelpModal } from "../help/HelpModalContext";
import { useLocalUser } from "../../pages/specialty-assistant/data";
import {
  fakeAssigneeNameForAssignee,
  usePrivacyMode,
} from "../../context-providers/Privacy";
import { ROUTES } from "../../routes";
import { useNavigate } from "react-router-dom";
import { useMapModal } from "../../pages/specialty-assistant/map/MapModalContext";

type PantheonAvatarProps = {
  /* If label is undefined, a question mark for "unassigned" will be displayed */
  label?: string;
  size?: "small" | "large";
  withMenu?: boolean;
  showName?: boolean;
};

// It's not enough to useMemo these because many use cases for Avatars get
// unmounted and remounted frequently, and these URls take several ms to
// generate.
const DATA_URL_CACHE: { [val: string]: string | undefined } = {};
export const generateIdenticonDataUrl = (val: string): string | undefined => {
  if (!DATA_URL_CACHE[val]) {
    // Try changes at https://jdenticon.com/icon-designer.html?config=ffffffff10c32843234c1840
    const jdenticonConfig = {
      hues: [195],
      lightness: {
        color: [0.35, 0.77],
        grayscale: [0.25, 0.65],
      },
      saturation: {
        color: 0.4,
        grayscale: 0.68,
      },
      backColor: "#fff",
    };
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 64;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      jdenticon.drawIcon(ctx, val, 64, jdenticonConfig);
      DATA_URL_CACHE[val] = canvas.toDataURL();
    } else {
      DATA_URL_CACHE[val] = undefined;
    }
  }
  return DATA_URL_CACHE[val];
};

const AvatarBase: React.FC<
  PantheonAvatarProps & { menuRef?: RefObject<Menu> }
> = ({ label, size, showName, menuRef, withMenu }) => {
  const tooltipId = useRef(`tooltip-${uuid()}`);
  const nameToShow = label ?? "Unassigned";
  const image =
    label !== undefined ? generateIdenticonDataUrl(label) : undefined;

  return (
    <>
      {!showName && (
        <Tooltip target={`#${tooltipId.current}`} position="left" />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Avatar
          shape="circle"
          className={classNames(
            styles.avatar,
            size === "small" ? styles.avatarSmall : styles.avatarLarge
          )}
          style={{ cursor: withMenu ? "pointer" : "default" }}
          icon="pi pi-question-circle"
          image={image}
          data-pr-tooltip={nameToShow}
          data-pr-showdelay={500}
          onClick={(e) => menuRef?.current?.toggle(e)}
          id={tooltipId.current}
        />
        {showName && (
          <span
            style={{
              fontSize: "12px",
              fontWeight: "bold",
              color: "var(--text-color)",
            }}
          >
            {nameToShow}
          </span>
        )}
      </div>
    </>
  );
};

export const PantheonAvatar: React.FC<PantheonAvatarProps> = ({
  label,
  size = "small",
  withMenu = false,
  showName = false,
}) => {
  const ref = useRef<Menu>(null);
  const { showHelpModal } = useHelpModal();
  const { showMapModal } = useMapModal();
  const localUser = useLocalUser();
  const navigate = useNavigate();

  const { privacyModeEnabled, togglePrivacyMode } = usePrivacyMode();

  const optionalMenuItems = [
    {
      template: () => (
        <div className={styles.avatarInMenuContainer}>
          <AvatarBase
            label={label}
            size="small"
            showName={true}
            withMenu={false}
          />
          <div className={styles.line} />
        </div>
      ),
    },
    localUser?.email === "pantheon_test_user" ||
    localUser?.email.endsWith("@pantheonai.co") ||
    [
      "calaoui@blueskyrisk.com",
      "jshewbert@blueskyrisk.com",
      "shartshorn@blueskyrisk.com",
      "cmcelwee@blueskyrisk.com",
    ].includes(localUser?.email ?? "")
      ? {
          label: "Analytics",
          icon: "pi pi-chart-line",
          command: () => navigate(ROUTES.DASHBOARD_ANALYTICS),
        }
      : undefined,
    localUser?.email === "pantheon_test_user" ||
    localUser?.email.endsWith("@blueskyrisk.com") ||
    localUser?.email.endsWith("@pantheonai.co")
      ? {
          label: "Search Bound Policies",
          icon: "pi pi-map",
          command: showMapModal,
        }
      : undefined,
    {
      label: "Feedback",
      icon: "pi pi-comments",
      command: () => {
        window.open("https://pantheon-ai.supahub.com/", "_blank");
      },
    },
    {
      label: "Help",
      icon: "pi pi-question-circle",
      command: () => {
        showHelpModal && showHelpModal();
      },
    },
    {
      label: "Sign out",
      icon: "pi pi-sign-out",
      command: () => {
        Token.logOut();
        window.location.reload();
      },
    },
    localUser?.email === "pantheon_test_user" ||
    localUser?.email.endsWith("@pantheonai.co")
      ? {
          label: "Privacy Mode",
          icon: privacyModeEnabled ? "pi pi-eye" : "pi pi-eye-slash",
          command: () => {
            togglePrivacyMode();
          },
        }
      : undefined,
  ];

  const allMenuItems = optionalMenuItems.filter((x) => !!x);

  return (
    <>
      {withMenu && <Menu model={allMenuItems} popup ref={ref} />}
      <AvatarBase
        label={
          privacyModeEnabled
            ? label
              ? fakeAssigneeNameForAssignee(label)
              : label
            : label
        }
        size={size}
        showName={showName}
        menuRef={ref}
        withMenu={withMenu}
      />
    </>
  );
};
